import isEmpty from 'lodash/isEmpty';
import { IConsumerDataResponse } from '../../consumerUxlApis/types/consumerUXlResponse.types';
import { IAriesSearchCriteria, SessionData, IAriesReservation, IAvailabilityRequestVO } from '../../session';
import { PAGE_URI } from '../../constants';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
// returning search specific datalayer keys
export const getSearchKeys = (
  sessionData: SessionData,
  pageURI = '',
  consumerData: IConsumerDataResponse | undefined
) => {
  const searchSessionKeys = getSearchSessionKeys(sessionData, pageURI);
  const reservationSessionKeys = getReservationSessionKeys(sessionData, pageURI, consumerData);
  const searchCriteriaKeys = getSearchCriteriaKeys(sessionData, searchSessionKeys);
  const weekendStayKey = getWeekendStaysKey(sessionData, pageURI, consumerData);
  const datalayerKeys = { ...searchSessionKeys, ...reservationSessionKeys, ...searchCriteriaKeys, ...weekendStayKey };
  // removing empty and undefined keys
  const filteredKeys = Object.fromEntries(
    Object.entries(datalayerKeys).filter(([_, value]) => value != null && value !== '')
  );

  return { ...filteredKeys };
};

const getSearchSessionKeys = (sessionData: SessionData, pageURI = '') => {
  if (!sessionData?.data?.AriesSearch)
    return {
      search_currency_type: 'Default',
    };
  const searchData = sessionData?.data?.AriesSearch;
  const searchCriteria = searchData.searchCriteria;

  const getClusterCode = () => {
    const availabilityRequestVO = searchCriteria?.availabilityRequestVO;
    if (!availabilityRequestVO) return '';

    const sessionClusterCode = availabilityRequestVO.clusterCode?.toLowerCase();
    if (availabilityRequestVO.redemptionClusterCode) {
      return availabilityRequestVO.redemptionClusterCode.toUpperCase();
    } else if (sessionClusterCode === 'corp') {
      return availabilityRequestVO.corporateCode?.toUpperCase();
    } else if (sessionClusterCode && sessionClusterCode !== 'none' && sessionClusterCode !== 'group') {
      return sessionClusterCode.toUpperCase();
    } else return '';
  };

  const getSearchBrands = () => {
    const brandCodes = searchData.brandCodes;
    if (!brandCodes) return 'all|';

    return typeof brandCodes === 'string' ? `${brandCodes}|` : `${brandCodes.join('|')}|`;
  };

  const getAvailabilitySearch = () => {
    if (searchData.search_availability_search) {
      return searchData.search_availability_search;
    }
    const availabilityRequestVO = searchCriteria?.availabilityRequestVO;
    if (
      availabilityRequestVO &&
      availabilityRequestVO.checkInDate &&
      availabilityRequestVO.checkInDate &&
      availabilityRequestVO.flexibleDate === false
    ) {
      return 'true';
    }
    return '';
  };

  const getGuestsPerRoom = () => {
    const guestsPerRoom = searchCriteria?.availabilityRequestVO?.numGuestsPerRoom;
    if (pageURI?.includes(PAGE_URI.defaultSearch) && guestsPerRoom && guestsPerRoom > 0) {
      return guestsPerRoom?.toString();
    } else {
      return guestsPerRoom?.toString() || '';
    }
  };

  const getAddressKeys = () => {
    const address = searchCriteria?.address;
    let googlePlacesDest = searchData.search_google_places_destination;
    if (!address) return {};

    const { city, stateProvince: state, country, destination } = address;
    const airportCode = searchCriteria?.airportCode;
    let addressKeys = {
      search_destination_city_state_country: country?.length ? `${city},${state},${country}` : '',
      search_destination_country: country,
      search_destination_state: state,
      search_destination_city: city,
      search_destination_airport_code: airportCode ?? '',
      search_google_places_destination: googlePlacesDest || '',
    };
    let destinationCity, destinationState, destinationCountry;
    if (destination && !city && !state && !country) {
      const addressTokens = destination.split(',');
      if (addressTokens.length == 2) {
        destinationCity = addressTokens[addressTokens.length - 2];
      } else if (addressTokens.length >= 3) {
        destinationCity = addressTokens[addressTokens.length - 3];
        destinationState = addressTokens[addressTokens.length - 2];
      }
      destinationCountry = addressTokens[addressTokens.length - 1];

      if (!googlePlacesDest) {
        googlePlacesDest = `"Name:${destinationCity}|Locality:${destinationCity}|State:${destinationState}|Postal Code:|Country:${destinationCountry}|Latitude:${
          address.latitude || ''
        }|Longitude:${address.longitude || ''}|Types:|Address:${destination}"`;
      }

      addressKeys = {
        ...addressKeys,
        search_destination_country: destinationCountry,
        search_destination_state: destinationState,
        search_destination_city: destinationCity,
        search_destination_city_state_country: destination,
        search_google_places_destination: googlePlacesDest || '',
      };
    }

    return addressKeys;
  };

  const getPaginationAndSortingKeys = () => {
    const paginationObj = searchCriteria?.paginationAndSortingRequestVO;
    if (searchCriteria && paginationObj) {
      return {
        search_impression_criteria3: `${paginationObj.sortType ?? ''};${
          typeof paginationObj.pageNumber === 'string' ? paginationObj.pageNumber : null
        };`,
        search_list_number_of_page: paginationObj.numberOfPages ?? '',
        search_list_records_on_page: paginationObj.recordsOnPage?.toString() ?? '',
        search_list_records_total: paginationObj.numberOfRecords?.toString() ?? '0',
        search_list_setting_sort_order: paginationObj.sortType || paginationObj.currentSortOrder || '',
        search_list_page_current: paginationObj.currentPage?.toString() || paginationObj.pageNumber?.toString() || '',
      };
    } else return {};
  };
  return {
    search_advance_purchase_days: searchData.search_advance_purchase_days,
    search_dates_flexible: searchData.search_dates_flexible ?? 'false',
    search_is_dates_flexible: searchData.search_dates_flexible ?? 'false',
    search_date_type: searchData.search_date_type,
    search_date_check_out: formatDate(searchCriteria?.availabilityRequestVO?.checkOutDate),
    search_date_check_in: formatDate(searchCriteria?.availabilityRequestVO?.checkInDate),
    search_date_check_in_day_of_week: searchData.search_date_check_in_day_of_week,
    search_day_check_in: searchData.search_date_check_in_day_of_week?.slice(0, 3)?.toLowerCase(),
    search_day_check_out: searchData.search_date_check_out_day_of_week?.slice(0, 3)?.toLowerCase(),
    search_currency_type: searchCriteria?.selectedCurrency ?? 'Default',
    search_cluster_code: getClusterCode() || '',
    search_brands: getSearchBrands(),
    search_availability_search: getAvailabilitySearch(),
    search_destination_lat_long: searchCriteria?.geoCode,
    search_number_guest: searchCriteria?.availabilityRequestVO?.numGuestsPerRoom?.toString(),
    search_guests_per_room: getGuestsPerRoom(),
    search_is_rewards_redemption: searchCriteria?.availabilityRequestVO?.rewardsRedemption?.toString() ?? '',
    search_keyword: searchCriteria && searchData.search_keyword ? searchData.search_keyword : '',

    ...getAddressKeys(),
    ...getPaginationAndSortingKeys(),
    ...getSearchSquadKeys(
      sessionData,
      searchCriteria,
      searchCriteria?.availabilityRequestVO,
      sessionData?.data?.AriesReservation,
      pageURI
    ),
  };
};

const getReservationSessionKeys = (
  sessionData: SessionData,
  pageURI = '',
  consumerData: IConsumerDataResponse | undefined
) => {
  const reservationData = sessionData?.data?.AriesReservation;
  if (!reservationData) {
    return {};
  }

  const getRateType = () => {
    if (!pageURI.includes(PAGE_URI.reviewDetails)) {
      return '';
    }
    const selectedRateType = reservationData?.selectedRateType;
    if (selectedRateType && selectedRateType.toLowerCase() === 'redemption') {
      return 'Lowest';
    } else {
      let selectedRate = getAverageRatePerNight(consumerData);
      if (selectedRate && selectedRate.includes(',')) {
        selectedRate = selectedRate.replace(',', '');
      }
      const lowestRate = reservationData?.lowestRateValue;
      if (selectedRate && lowestRate && lowestRate === selectedRate) {
        return 'Lowest';
      } else return 'Not Lowest';
    }
  };

  const getHotelAvailability = () => {
    if (
      pageURI.includes(PAGE_URI.availabilityCalendar) ||
      pageURI.includes(PAGE_URI.modifyRLM) ||
      pageURI.includes(PAGE_URI.upgradeReservation)
    ) {
      return reservationData.hwsAvailabilitySearchCount ?? '0';
    }
    return '';
  };
  return {
    search_hotel_availability: getHotelAvailability(),
    search_multiRate_lowest_rate: getRateType(),
  };
};

const getAverageRatePerNight = (consumerData: IConsumerDataResponse | undefined) => {
  let numberOfRooms = 0;
  let subtotal = 0;
  let numberOfNights = 0;
  let averagePricePerNight = '';
  const cartItemsNode = consumerData?.cartDetails?.cart?.items;
  if (cartItemsNode?.length) {
    const totalPricingNode = cartItemsNode[0].totalPricing;
    numberOfRooms += totalPricingNode?.quantity || 0;
    const productNode = cartItemsNode[0].product;
    numberOfNights = productNode?.basicInformation?.lengthOfStay || 0;
    const totalPricingAmountsArray = totalPricingNode?.rateAmounts;
    const subTotalAmountNode = totalPricingAmountsArray?.find(obj => obj.rateMode?.code === 'sub-total-per-quantity');
    subtotal = subTotalAmountNode?.amount?.origin?.value || 0;
    const productRates = productNode?.rates;
    const rateAmountsArray = productRates?.rateAmounts;
    const rateAmount = rateAmountsArray?.find(obj => obj.rateMode?.code === 'average-nightly-rate-per-unit');
    const averageNightlyRateDecimalPoint = rateAmount?.amount?.origin?.valueDecimalPoint;
    const totalStayValue = numberOfRooms * numberOfNights;
    if (totalStayValue > 0 && subtotal != null) {
      let averageRates = subtotal / totalStayValue;
      averageRates = roundHalfEven(averageRates, 2); // rounding to 2 decimal points using HALF_EVEN

      if (averageNightlyRateDecimalPoint && averageNightlyRateDecimalPoint > 0) {
        averageRates = roundHalfEven(
          averageRates / Math.pow(10, averageNightlyRateDecimalPoint),
          averageNightlyRateDecimalPoint
        );
      } else averageRates = roundHalfEven(averageRates / 100, 2);

      averagePricePerNight = averageRates.toString();
    }
  }
  return averagePricePerNight;
};

const formatDate = (date = '') => {
  return date ? dayjs(date).format('MM/DD/YYYY') : '';
};

const roundHalfEven = (num: number, decimalPlaces: number) => {
  const factor = Math.pow(10, decimalPlaces);
  const n = num * factor;
  const rounded = Math.round(n);
  const diff = Math.abs(n - rounded);

  if (diff === 0.5) {
    return (Math.floor(n) % 2 === 0 ? Math.floor(n) : Math.ceil(n)) / factor;
  }
  return rounded / factor;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSearchCriteriaKeys = (sessionData: SessionData, datalayerObj: Record<string, any>) => {
  if (datalayerObj && datalayerObj['search_destination_city_state_country']?.length) {
    const searchCriteria = `CHECKIN_DATE=${datalayerObj['search_date_check_in']};NUMBER_OF_GUESTPERROOMS=${
      datalayerObj['search_number_guest']
    };STATE=${datalayerObj['search_destination_state']};CLUSTER_CODE=${
      datalayerObj['search_cluster_code']?.length ? datalayerObj['search_cluster_code'] : 'none'
    };NUMBER_OF_ROOMS=${sessionData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.numRooms};COUNTRY=${
      datalayerObj['search_destination_country']
    };CHECKOUT_DATE=${datalayerObj['search_date_check_out']};CITY=${datalayerObj['search_destination_city']}`;
    return { search_criteria: searchCriteria };
  }
  return {};
};

const getWeekendStaysKey = (
  sessionData: SessionData,
  pageURI = '',
  consumerData: IConsumerDataResponse | undefined
) => {
  if (!pageURI.includes(PAGE_URI.confirmReservationCancellation) && !pageURI.includes(PAGE_URI.upcomingReservation)) {
    return { search_is_weekend_stay: sessionData?.data?.AriesSearch?.search_is_weekend_stay ?? '' };
  }
  if (isEmpty(consumerData)) {
    return {};
  }
  const basicInfo = consumerData?.orderDetails?.order?.items?.[0].basicInformation;
  const startDate = basicInfo?.startDate;
  const endDate = basicInfo?.endDate;

  // Convert input to Day.js objects
  let start = dayjs(startDate);
  const end = dayjs(endDate);

  let hasWeekend = false;
  let hasWeekday = false;

  // Iterate through each day in the range
  while (start.isBefore(end) || start.isSame(end)) {
    const dayOfWeek = start.day(); // Get the day as a number (0=Sunday, 6=Saturday)
    if (dayOfWeek === 5 || dayOfWeek === 6) {
      hasWeekend = true; // It's a weekend
    } else {
      hasWeekday = true; // It's a weekday
    }
    // Break early if both are found
    if (hasWeekend && hasWeekday) {
      return { search_is_weekend_stay: 'Weekday and Weekend Stay' };
    }
    start = start.add(1, 'day'); // Move to the next day
  }

  // Determine the result
  if (hasWeekend && !hasWeekday) {
    return { search_is_weekend_stay: 'Weekend Only' };
  } else if (!hasWeekend && hasWeekday) {
    return { search_is_weekend_stay: 'Weekdays Only' };
  } else {
    return { search_is_weekend_stay: 'Weekday and Weekend Stay' };
  }
};

const getSearchSquadKeys = (
  sessionData: SessionData,
  searchCriteria: IAriesSearchCriteria | undefined,
  availRVO: IAvailabilityRequestVO | undefined,
  ariesReservation: IAriesReservation | undefined,
  pageURI: string
) => {
  const searchSquadKeys: Record<string, string | number | boolean | undefined> = {};
  let searchRatesTypes = '';
  const page_requested_uri = window?.location?.pathname?.replace('.mi', '');
  searchSquadKeys['search_number_guest'] = availRVO?.numGuestsPerRoom?.toString();
  searchSquadKeys['search_previous_destination_city'] = searchCriteria?.address?.city;
  searchSquadKeys['search_previous_destination_country'] = searchCriteria?.address?.country;
  searchSquadKeys['search_previous_destination_state'] = searchCriteria?.address?.stateProvince;

  if (availRVO?.multiRateSearch === true && availRVO?.flexibleDate == true) {
    searchSquadKeys['search_res_path_type'] = 'Multi-Rate Enabled Search';
  }

  if (
    (page_requested_uri && page_requested_uri == '/search/findHotels') ||
    (page_requested_uri == '/search/filterSearch' && sessionData?.data?.AriesSearch)
  ) {
    searchSquadKeys['search_show_available_hotels'] = searchCriteria?.showAvailableHotels ?? '';
  }

  if (
    pageURI?.includes(PAGE_URI.modifyRLM) ||
    (pageURI?.includes(PAGE_URI.upgradeReservation) && ariesReservation) ||
    pageURI?.includes(PAGE_URI.availabilityCalendar)
  ) {
    searchSquadKeys['search_source_hws'] = ariesReservation?.availabilitySearchSourceHWS as string;
  }

  const getSearchResPathType = () => {
    if (pageURI?.includes(PAGE_URI.modifyRLM) || (pageURI?.includes(PAGE_URI.upgradeReservation) && ariesReservation)) {
      if (availRVO?.multiRateSearch == false && ariesReservation?.newUI) {
        searchSquadKeys['search_res_path_type'] = 'New Search without Multi-Rate';
      } else if (ariesReservation?.newUI == null) {
        searchSquadKeys['search_res_path_type'] = 'Legacy Search';
      }
    }
  };

  const getSearchRateTypesAndClusterCode = () => {
    if (availRVO?.multiRateSearch === true) {
      const corpCodeData = availRVO?.corpCodeVOList;
      const multiRateClusterCodeList: string[] = [];
      corpCodeData?.forEach(item => {
        if (item?.corpCode) multiRateClusterCodeList?.push(item.corpCode);
      });

      availRVO?.multiRateClusterCodes?.forEach(code => multiRateClusterCodeList?.push(code));

      if (multiRateClusterCodeList?.length > 0) {
        searchRatesTypes = 'Special Rates,';
      }

      if (availRVO?.multiRateRewardsRedemption == true) {
        multiRateClusterCodeList?.push('MW1');
        searchRatesTypes += 'Points,';
      }

      if (availRVO?.lowestRegularRate) {
        multiRateClusterCodeList?.push('LARE');
        searchRatesTypes += 'Standard Rates,';
      }

      const numOfRates = multiRateClusterCodeList?.length;

      if (numOfRates > 0) {
        searchSquadKeys['search_cluster_code'] = multiRateClusterCodeList?.toString();
      }

      if (sessionData?.data?.['x-request-id'] == '/search/findHotels.mi') {
        searchSquadKeys['search_rate_types'] = searchRatesTypes;
        searchSquadKeys['search_number_of_rates_submitted'] = numOfRates;
      }

      if (availRVO?.multiRateSearch === true) {
        if (numOfRates == 1) {
          searchSquadKeys['search_multiRate_or_single'] = 'Single Rate Search';
        } else if (numOfRates > 1) {
          searchSquadKeys['search_multiRate_or_single'] = 'Multi-Rate Search';
        }
      }
    }
  };

  const getSearchType = () => {
    if (pageURI?.includes(PAGE_URI.rlm) && ariesReservation !== null && Cookies.get('updatedSearchType')) {
      searchSquadKeys['search_type'] = Cookies.get('updatedSearchType')?.split('=')?.[1] as string;
    } else {
      searchSquadKeys['search_type'] =
        Boolean(ariesReservation?.availabilitySearchSourceHWS) === true ? 'From HWS' : '';
    }
  };

  const getSearchMultiRateOrSingle = () => {
    if (
      pageURI?.includes(PAGE_URI.modifyRLM) ||
      (pageURI?.includes(PAGE_URI.upgradeReservation) && ariesReservation != null)
    ) {
      if (availRVO?.rewardsRedemption === true || availRVO?.clusterCode) {
        searchSquadKeys['search_multiRate_or_single'] = 'Multi-Rate Search';
      } else {
        searchSquadKeys['search_multiRate_or_single'] = 'Single Rate Search';
      }
    }
  };

  getSearchResPathType();
  getSearchRateTypesAndClusterCode();
  getSearchType();
  getSearchMultiRateOrSingle();

  return { ...searchSquadKeys };
};
